import React, { useState } from 'react';
import { useOrderFlowStore } from '../../../store/orderFlowStore';
import { useTranslations } from '../../../hooks/useTranslations';
import { useCustomerInfo } from '../../../hooks/useCustomerInfo';
import { prepareLineItemProperties, submitOrderWithFiles, getCartErrorMessage, isRetryableError } from '../../../lib/shopifyApi';
import styles from './OrderConfirmation.module.scss';
import PriceDisplay from '../../../components/molecules/PriceDisplay/PriceDisplay';
import { ProductPreview } from '../components/ProductPreview';

interface OrderConfirmationProps {
  onOrderProceed: () => void;
  onRestart: () => void;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  onOrderProceed,
  onRestart
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const customerInfo = useCustomerInfo();
  
  const {
    productType,
    beanType,
    packageType,
    selectedBean,
    lotSize,
    sugarConcentration,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsOuterBoxDesign,
    needsBackLabel,
    totalPrice,
    basePrice,
    currentProduct,
    innerDesignFile,
    frontLabelFile,
    outerBoxDesignFile,
    backLabelFile,
    optionPrices,
    validateUploadedFiles
  } = useOrderFlowStore();

  const translations = useTranslations();

  // エラー状態をリセット
  const resetErrorState = () => {
    setError(null);
    setShowRetryButton(false);
  };

  // カートに追加する処理（ファイル連携も含む）
  const handleProceedToCheckout = async () => {
    resetErrorState();
    // 承認チェック
    if (!customerInfo.canOrder) {
      setError(translations.orderConfirm_orderRequiresApproval);
      return;
    }

    if (!currentProduct || !lotSize) {
      setError(translations.orderConfirm_missingProductInfo);
      return;
    }

    // ファイルのバリデーション
    const validation = validateUploadedFiles();
    if (!validation.isValid) {
      setError(translations.orderConfirm_fileErrors + validation.errors.join('\n'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // ファイル連携を含む注文処理を実行
      const variant = currentProduct.productInfo.variants.find(v => v.lotSize === lotSize);
      if (!variant) {
        throw new Error(translations.orderConfirm_noMatchingVariant);
      }

      // LINE ITEM PROPERTYを準備
      const uploadedFiles = {
        innerDesignFile,
        frontLabelFile,
        outerBoxDesignFile,
        backLabelFile
      };

      const orderData = {
        productType,
        beanType,
        packageType,
        selectedBean,
        lotSize,
        sugarConcentration,
        needsInnerDesign,
        needsFrontLabel,
        needsOuterBox,
        needsOuterBoxDesign,
        needsBackLabel
      };

      const properties = prepareLineItemProperties(orderData, uploadedFiles);

      // オプション商品を準備
      const additionalItems: any[] = [];
      const optionSet = currentProduct.optionSetReference;
      
      if (optionSet) {
        
        // インナーデザイン（フリーズドライ・スティック用）
        if (productType === 'freeze-dried' && packageType === 'stick' && needsInnerDesign) {
          const innerDesignOptions = optionSet.fd_inner_design_stick;
          if (innerDesignOptions) {
            const innerDesignVariant = innerDesignOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (innerDesignVariant) {
              additionalItems.push({
                id: innerDesignVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

        // 外箱（フリーズドライ用）
        if (productType === 'freeze-dried' && needsOuterBox) {
          const outerBoxOptions = optionSet.fd_outer_box;
          if (outerBoxOptions) {
            const outerBoxVariant = outerBoxOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (outerBoxVariant) {
              additionalItems.push({
                id: outerBoxVariant.gid.toString(),
                quantity: 1
              });
            }
          }

          // 外箱デザイン
          if (needsOuterBoxDesign) {
            const outerBoxDesignOptions = optionSet.fd_outer_box_design;
            if (outerBoxDesignOptions) {
              const outerBoxDesignVariant = outerBoxDesignOptions.find(option => 
                option.title === lotSize && option.available
              );
              
              if (outerBoxDesignVariant) {
                additionalItems.push({
                  id: outerBoxDesignVariant.gid.toString(),
                  quantity: 1
                });
              }
            }
          }
        }

        // 表ラベル（フリーズドライ・キューブ用）
        if (productType === 'freeze-dried' && packageType === 'cube' && needsFrontLabel) {
          const frontLabelOptions = optionSet.fd_front_label_cube;
          if (frontLabelOptions) {
            const frontLabelVariant = frontLabelOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (frontLabelVariant) {
              additionalItems.push({
                id: frontLabelVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

        // 裏ラベルデザイン（フリーズドライ用）
        if (productType === 'freeze-dried' && needsBackLabel) {
          const backLabelOptions = optionSet.fd_back_label_design;
          if (backLabelOptions) {
            const backLabelVariant = backLabelOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (backLabelVariant) {
              additionalItems.push({
                id: backLabelVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

        // === 濃縮液・ストレートコーヒー用オプション ===
        
        // 砂糖濃度オプション（濃縮液・ストレート用）
        if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && 
            sugarConcentration && sugarConcentration !== 'none') {
          
          const sugarOptionKey = productType === 'liquid-concentrate' ? 'nk_sugar' : 'st_sugar';
          const sugarOptions = optionSet[sugarOptionKey];
          
          if (sugarOptions) {
            const sugarVariant = sugarOptions.find(option => {
              const titleLower = option.title.toLowerCase();
              return titleLower.includes(lotSize) && 
                     (titleLower.includes(`${sugarConcentration}%`) || titleLower.includes(sugarConcentration));
            });
            
            if (sugarVariant && sugarVariant.available) {
              additionalItems.push({
                id: sugarVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

        // 表ラベル（濃縮液・ストレート用）
        if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && needsFrontLabel) {
          const frontLabelOptionKey = productType === 'liquid-concentrate' ? 'nk_front_label' : 'st_front_label';
          const frontLabelOptions = optionSet[frontLabelOptionKey];
          
          if (frontLabelOptions) {
            const frontLabelVariant = frontLabelOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (frontLabelVariant) {
              additionalItems.push({
                id: frontLabelVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

        // 外箱（濃縮液・ストレート用）
        if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && needsOuterBox) {
          const outerBoxOptionKey = productType === 'liquid-concentrate' ? 'nk_outer_box' : 'st_outer_box';
          const outerBoxOptions = optionSet[outerBoxOptionKey];
          
          if (outerBoxOptions) {
            const outerBoxVariant = outerBoxOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (outerBoxVariant) {
              additionalItems.push({
                id: outerBoxVariant.gid.toString(),
                quantity: 1
              });
            }
          }

          // 外箱デザイン（濃縮液・ストレート用）
          if (needsOuterBoxDesign) {
            const outerBoxDesignOptionKey = productType === 'liquid-concentrate' ? 'nk_outer_box_design' : 'st_outer_box_design';
            const outerBoxDesignOptions = optionSet[outerBoxDesignOptionKey];
            
            if (outerBoxDesignOptions) {
              const outerBoxDesignVariant = outerBoxDesignOptions.find(option => 
                option.title === lotSize && option.available
              );
              
              if (outerBoxDesignVariant) {
                additionalItems.push({
                  id: outerBoxDesignVariant.gid.toString(),
                  quantity: 1
                });
              }
            }
          }
        }

        // 裏ラベルデザイン（濃縮液・ストレート用）
        if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && needsBackLabel) {
          const backLabelOptionKey = productType === 'liquid-concentrate' ? 'nk_back_label_design' : 'st_back_label_design';
          const backLabelOptions = optionSet[backLabelOptionKey];
          
          if (backLabelOptions) {
            const backLabelVariant = backLabelOptions.find(option => 
              option.title === lotSize && option.available
            );
            
            if (backLabelVariant) {
              additionalItems.push({
                id: backLabelVariant.gid.toString(),
                quantity: 1
              });
            }
          }
        }

      }

      // Liquidフォーム経由でカートに追加（ファイル連携 + オプション商品込み）
      await submitOrderWithFiles(
        variant.id,
        properties,
        uploadedFiles,
        additionalItems
      );

      // submitOrderWithFilesは内部でフォーム送信とページ遷移を行うため、
      // ここで明示的なリダイレクトは不要
      
    } catch (error) {
      console.error(translations.orderConfirm_orderProcessingError, error);
      
      // APIエラーの場合は適切なユーザーメッセージを表示
      if (error instanceof Error) {
        const userMessage = getCartErrorMessage(error, translations);
        setError(userMessage);
        
        // 再試行可能なエラーの場合は再試行ボタンを表示
        if (isRetryableError(error)) {
          setShowRetryButton(true);
        } else {
          setShowRetryButton(false);
        }
      } else {
        setError(translations.orderConfirm_unexpectedError);
        setShowRetryButton(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 注文ボタンの状態を判定
  const getOrderButtonState = () => {
    if (!customerInfo.isLoggedIn) {
      return {
        disabled: true,
        text: translations.orderConfirm_loginRequired,
        message: translations.orderConfirm_loginRequiredMessage
      };
    }
    
    if (customerInfo.isPendingApproval) {
      return {
        disabled: true,
        text: translations.orderConfirm_pendingApproval,
        message: translations.orderConfirm_pendingApprovalMessage
      };
    }
    
    if (customerInfo.canOrder) {
      return {
        disabled: isLoading,
        text: isLoading ? translations.orderConfirm_addingToCart : translations.orderConfirm_proceedToOrder,
        message: null
      };
    }
    
    return {
      disabled: true,
      text: translations.orderConfirm_cannotOrder,
      message: translations.orderConfirm_cannotOrderMessage
    };
  };

  const orderButtonState = getOrderButtonState();

  // 商品名の生成
  const getProductTitle = () => {
    if (!currentProduct) return '';
    
    const baseTitle = currentProduct.productInfo.title;
    const beanName = selectedBean ? `beans.${selectedBean}` : '';
    const packageName = packageType ? `package.${packageType}` : '';
    
    return `${baseTitle} ${beanName} ${packageName}`.trim();
  };

  // 価格内訳を生成
  const generatePriceBreakdown = () => {
    const breakdown: Array<{ label: string; value: number }> = [];
    
    // ベース商品
    if (basePrice > 0) {
      let baseLabel = '';
      if (productType === 'freeze-dried') {
        baseLabel = translations.productType_freezeDried;
      } else if (productType === 'liquid-concentrate') {
        baseLabel = translations.productType_liquidConcentrate;
      } else if (productType === 'straight-coffee') {
        baseLabel = translations.productType_straightCoffee;
      }
      
      if (beanType === 'wako' && selectedBean) {
        const beanLabel = selectedBean === 'wako-house-blend' ? translations.bean_houseBlend : translations.bean_houseDarkBlend;
        baseLabel += ` (${beanLabel})`;
      } else if (beanType === 'my-bean') {
        baseLabel += ` (${translations.bean_customerBean})`;
      }
      
      if (packageType) {
        const packageLabel = packageType === 'stick' ? translations.package_stick : 
                           packageType === 'cube' ? translations.package_cube :
                           packageType === 'spout' ? translations.package_spout : 
                           packageType === 'pouch' ? translations.package_pouch :
                           packageType === 'bottle' ? translations.package_bottle : '';
        baseLabel += ` (${packageLabel})`;
      }
      
      if (lotSize) {
        baseLabel += ` - ${lotSize}個`;
      }
      
      breakdown.push({ label: baseLabel, value: basePrice });
    }
    
    // 砂糖濃度
    if (sugarConcentration && sugarConcentration !== 'none') {
      const sugarPrice = optionPrices[`sugar_${sugarConcentration}`] || 0;
      breakdown.push({ label: `${translations.priceBreakdown_sugarConcentration} ${sugarConcentration}%`, value: sugarPrice });
    }
    
    // インナーデザイン
    if (needsInnerDesign) {
      const designType = packageType === 'stick' ? translations.design_innerDesignStick : translations.design_innerFrontLabelCube;
      const innerPrice = optionPrices['inner_design'] || 0;
      breakdown.push({ label: designType, value: innerPrice });
    }
    
    // 表ラベル
    if (needsFrontLabel) {
      const frontPrice = optionPrices['front_label'] || 0;
      breakdown.push({ label: translations.design_frontLabelDesign, value: frontPrice });
    }
    
    // 外箱
    if (needsOuterBox) {
      const outerLabel = needsOuterBoxDesign ? translations.design_outerBoxWithDesign : translations.design_outerBoxPlain;
      const outerPrice = optionPrices['outer_box'] || 0;
      breakdown.push({ label: outerLabel, value: outerPrice });
    }
    
    // 裏ラベル
    if (needsBackLabel) {
      const backPrice = optionPrices['back_label'] || 0;
      breakdown.push({ label: translations.design_backLabelDesign, value: backPrice });
    }
    
    return breakdown;
  };

  return (
    <div className={styles.container}>
      {/* 背景 */}
      <div className={styles.background} />

      {/* メイン商品画像エリア */}
      <div className={styles.productSection}>
        <div className={styles.productImage}>
          <div className={styles.previewOverlay}>
            <ProductPreview />
          </div>
        </div>

        <div className={styles.productInfo}>
          <h1 className={styles.title}>
            {translations.orderConfirm_orderComplete}
          </h1>
          {/* アクションボタンエリア */}
          <div className={styles.actionsSection}>
            {/* エラー表示 */}
            {error && (
              <div className={styles.errorMessage}>
                <p>{error}</p>
                {/* 再試行ボタン */}
                {showRetryButton && (
                  <button
                    className={styles.retryButton}
                    onClick={handleProceedToCheckout}
                    disabled={isLoading}
                  >
                    {translations.orderConfirm_retryOrder || 'もう一度試す'}
                  </button>
                )}
              </div>
            )}

            {/* 承認状態メッセージ */}
            {orderButtonState.message && (
              <div className={styles.statusMessage}>
                <p>{orderButtonState.message}</p>
              </div>
            )}

            {/* メインボタン */}
            <button 
              className={styles.proceedButton}
              onClick={handleProceedToCheckout}
              disabled={orderButtonState.disabled}
            >
              <span>
                {orderButtonState.text}
              </span>
              {!orderButtonState.disabled && (
                <div className={styles.arrowIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M13 7L18 12L13 17M6 12H18" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </button>

            {/* リスタートボタン */}
            <button 
              className={styles.restartButton}
              onClick={onRestart}
              disabled={isLoading}
            >
              <span>{translations.orderConfirm_restartFromBeginning}</span>
              <div className={styles.replayIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M4 4V8H8M20 20V16H16M20.49 9A9 9 0 0 0 5.64 5.64L4 8M3.51 15A9 9 0 0 0 18.36 18.36L20 16" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>


      {/* 価格表示 */}
      <div className={styles.priceSection}>
        <PriceDisplay 
          price={totalPrice} 
          breakdown={generatePriceBreakdown()} 
          isProvisional={false}
        />
      </div>
    </div>
  );
};

export default OrderConfirmation; 